/**
 * Style Grid Component
 *
 * Displays a grid of photo style options for the Pickple AI Photo Studio.
 * Each style card shows a preview image with hover effects revealing the style details.
 * On devices without hover capability (mobile), style details are always visible.
 */
import { useEffect, useState } from "react";

/**
 * Background mode types for different photo styles
 */
export type BackgroundMode =
  | "beauty_grid"
  | "underwater"
  | "high_key"
  | "beauty_couple"
  | "with_pet"
  | "id_photo"
  | "youth_drama"
  | "disposable"
  | "once_upon_a_time"
  | "fireworks"
  | "family_portrait"
  | "animal_studio";

/**
 * Configuration interface for each style option matching the database schema
 */
export interface StyleConfig {
  style_id: BackgroundMode;
  title: string;
  subtitle: string;
  eng_title: string;
  image_url: string;
  samples: string[];
  badge?: string | null;
  is_visible: boolean;
  display_order: number;
}

interface StyleGridProps {
  /**
   * Array of styles to display, typically loaded from the database
   */
  styles: StyleConfig[];
  /**
   * Callback function when a style is selected
   */
  onStyleSelect?: (styleId: BackgroundMode) => void;
  /**
   * Array of style IDs that have pending/processing generations
   */
  pendingStyleIds?: string[];
}

/**
 * StyleGrid Component
 *
 * Renders a responsive grid of photo style cards with hover effects and badges.
 *
 * @param styles - Array of style configurations to display
 * @param onStyleSelect - Callback function triggered when a style is clicked
 */
export function StyleGrid({
  styles,
  onStyleSelect,
  pendingStyleIds = [],
}: StyleGridProps) {
  // Detect if device supports hover (PC browsers)
  const [canHover, setCanHover] = useState(true);

  useEffect(() => {
    // Check if device supports hover interaction
    const mediaQuery = window.matchMedia("(hover: hover)");
    setCanHover(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => setCanHover(e.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Database styles use is_visible (boolean)
  const visibleStyles = styles.filter((style) => style.is_visible);

  const handleStyleClick = (styleId: BackgroundMode) => {
    if (onStyleSelect) {
      onStyleSelect(styleId);
    }
  };

  return (
    <div className="grid w-full grid-cols-2 gap-[5px] border-b border-black/10 bg-white lg:grid-cols-3">
      {visibleStyles.map((style, index) => {
        const isPending = pendingStyleIds.includes(style.style_id);
        // First 6 images load eagerly (above the fold), rest lazy load
        const shouldLazyLoad = index >= 6;

        return (
          <button
            key={style.style_id}
            onClick={() => handleStyleClick(style.style_id)}
            className="group relative aspect-3/4 overflow-hidden bg-[#F3F3F1] focus:outline-none"
          >
            <img
              src={style.image_url}
              alt={style.title}
              loading={shouldLazyLoad ? "lazy" : "eager"}
              decoding="async"
              // First image is LCP - prioritize loading
              fetchPriority={index === 0 ? "high" : undefined}
              className={`h-full w-full object-cover transition-transform duration-700 ease-out ${canHover ? "group-hover:scale-105" : ""}`}
            />
            {/* Pending Generation Indicator */}
            {isPending && (
              <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 rounded-full bg-black/80 px-3 py-1.5 shadow-lg">
                <span className="h-2 w-2 animate-pulse rounded-full bg-yellow-400"></span>
                <span className="font-mono text-[9px] font-bold tracking-wider text-white uppercase">
                  Processing
                </span>
              </div>
            )}
            {canHover ? (
              // PC: Show on hover with dimmed overlay
              <div className="absolute inset-0 flex flex-col justify-between bg-black/20 p-6 opacity-0 transition-all duration-300 group-hover:opacity-100">
                <div className="flex items-start justify-between">
                  <span className="rounded bg-[#F3F3F1] px-2 py-0.5 font-mono text-[9px] font-bold tracking-widest text-black uppercase shadow-sm">
                    {style.eng_title}
                  </span>
                  {style.badge && (
                    <span className="rounded bg-[#E5E5E5] px-2 py-0.5 font-mono text-[9px] font-bold tracking-widest text-gray-800 uppercase shadow-sm">
                      {style.badge}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-left text-2xl leading-none font-black tracking-tight text-white uppercase drop-shadow-md md:text-3xl">
                    {style.title}
                  </h3>
                  <p className="mt-2 text-left font-mono text-xs tracking-wide text-white/90 uppercase drop-shadow-md">
                    {style.subtitle}
                  </p>
                </div>
              </div>
            ) : (
              // Mobile: Always visible with gradient overlay
              <>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-between p-4">
                  <div className="flex items-start justify-between">
                    <span className="rounded bg-[#F3F3F1]/90 px-2 py-0.5 font-mono text-[9px] font-bold tracking-widest text-black uppercase shadow-sm">
                      {style.eng_title}
                    </span>
                    {style.badge && (
                      <span className="rounded bg-[#E5E5E5]/90 px-2 py-0.5 font-mono text-[9px] font-bold tracking-widest text-gray-800 uppercase shadow-sm">
                        {style.badge}
                      </span>
                    )}
                  </div>
                  <div>
                    <h3 className="text-left text-xl leading-none font-black tracking-tight text-white uppercase drop-shadow-md">
                      {style.title}
                    </h3>
                    <p className="mt-1.5 text-left font-mono text-[10px] tracking-wide text-white/90 uppercase drop-shadow-md">
                      {style.subtitle}
                    </p>
                  </div>
                </div>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
